/**
 * The client's setup picture — the shape the server derives ONCE and both
 * surfaces render.
 *
 * ⚠️ IT LIVES IN @bas/shared BECAUSE TWO SURFACES READ IT: the Setup page and the
 * nav badge. The badge previously ran its own filter over a different model, and
 * the comment it replaced recorded that it was the FOURTH hand-kept copy of the
 * same rule — found only after a sweep had "consolidated the other three",
 * because that copy was spelled inverted. Four copies is the argument for zero.
 * [[feedback_two_lists_kept_in_agreement_by_hand]]
 *
 * Mirrors `backend/functions/src/agents/skillRoadmapPage.ts`.
 */
export type GateStatusMark = 'ok' | 'wait' | 'sign' | 'build' | 'open'

export type RoadmapState =
  | 'done' | 'now' | 'wait' | 'building' | 'signoff' | 'later' | 'not-hired'

export interface RoadmapGate {
  label: string
  status: GateStatusMark
  who?: string
}

export interface RoadmapSection {
  title: string
  subtitle: string
  state: RoadmapState
  gates: RoadmapGate[]
  cta?: string
  then?: string
  eta?: string
  etaIsWait?: boolean
  /**
   * ⚠️ `next` AND `since` WERE MISSING HERE (found 2026-09-22). The server has emitted
   * both for some time — `next` is the one-sentence "where you are, what's next" John
   * asked for on 09-17 after saying he could not read the dots, and `since` is the
   * waiting-on-you line. This type never declared them, so anything rendering from it
   * could not show them and nothing failed. Same mirror-drift class as ProfileCategory
   * (three copies) and the previews contingency (invented field names, shipped blank).
   */
  next?: string
  since?: string
  /**
   * The committed monthly ad spend, ads card only (John, 2026-09-22 CC review #22).
   * `note` says where the money goes and must always render with the amount — a money
   * figure on our page with no such line reads as OUR charge.
   */
  adBudget?: { amount: string; note: string }
}

/** One real step on a box — the unit the client actually works through. */
export interface RoadmapItem {
  label: string
  /**
   * ⚠️ `soon` IS NOT `now` AND MUST NOT LOOK LIKE IT. A client has not failed at
   * a step nobody has put in front of them, so it renders quiet and grey — never
   * amber, never a padlock.
   */
  state: 'done' | 'now' | 'wait' | 'soon'
  /** The button. Only ever on a `now` row. Absent = render text, never a dead control. */
  action?: { label: string; href: string }
  /** Right-aligned note for a row that is not theirs ("with the carriers"). */
  wait?: string
  /**
   * A line under a `now` row, read before the button — what the client will meet
   * on the provider's side (Google's "unverified app" screen on Google Ads).
   * Built server-side; the switch is backend onboarding/oauthConsentScreen.ts.
   */
  note?: string
  /** Opens the connected thing, when a URL identifies it specifically. */
  verifyUrl?: string
  /** "Not this one?" — re-point a connected target. */
  changeKindId?: string
  /** "No thanks" — the way out, on the rows that have one. */
  declineKey?: string
  /**
   * The built URLs for the two secondary links.
   *
   * ⚠️ BUILT SERVER-SIDE, WHERE THE TOKEN IS. The key says WHETHER a row has a
   * way out; the href says WHERE. A surface that assembled these itself would be
   * a second copy of the roadmap URL scheme.
   */
  changeHref?: string
  declineHref?: string
  /**
   * Which of the card's four parts this step belongs to. `activation` is the ONE
   * named step that turns the skill on (skillActivation.ts) — rendered inside the
   * go-ahead part, above the button it unlocks (below it when `afterGoAhead`).
   */
  kind?: 'info' | 'connection' | 'activation' | 'goahead'
  /** On an `activation` row: it follows the go-ahead instead of gating it (the web domain). */
  afterGoAhead?: boolean
  /** On the `goahead` row: the button's word — "Activate" where it makes the thing live. */
  buttonLabel?: 'Activate' | 'Approve'
  /**
   * The go-ahead, when it can be given now: POST `{ action: 'approve' | 'not-yet',
   * notes? }` to `href` and update the row IN PLACE (John, 2026-09-24).
   * `reviewHref` opens the full page where the thing is tried (the chat, the site).
   */
  approve?: { href: string; reviewHref?: string; reviewLabel?: string }
  /** The go-ahead, when it cannot be given yet — always with the reason and whose move. */
  blocked?: { reason: string; who: 'client' | 'us' | 'external' }
  /** The go-ahead was given — the card collapses to "✓ Live". */
  live?: boolean
}

/** A sample row, exactly as the previews builder returns it (see usePreviews.PreviewRow). */
export interface RoadmapMadeRow {
  title: string
  subtitle?: string
  html: string
  channel?: string
  contingency?: { badge: string; note: string; tone?: 'wait' | 'good' }
  action?:
    | { kind: 'artifact' }
    | { kind: 'artifact-page'; href: string; cta: string }
    | { kind: 'test-send'; cta: string; sendId: string }
    | { kind: 'experiential'; instruction: string; phone?: string }
    | { kind: 'asset'; href?: string; value?: string; cta?: string }
  /** Built on a question they have not answered — say so, with one door to it (#9). */
  pending?: { note: string; cta: string; href?: string }
}

/** The phone card's own parts. Mirrors skillRoadmapPage.PhoneCardExtras. */
export interface RoadmapPhoneExtras {
  tryNumber?: string
  tryNumberE164?: string
  direct: boolean
  greetingAudio?: { known: string; unknown: string }
  ownerTest?: { atMs?: number; seconds?: number; callId?: string; callHref?: string }
  /** The receptionist's pronouns, from the voice they chose — never a guessed 'her'. */
  pronouns?: { subj: string; obj: string; poss: string; known: boolean }

  forwardingObserved?: boolean
}

/** "Your booking page" in the paperwork. Mirrors skillCards.BookingPageRow. */
export interface RoadmapBookingRow {
  on: boolean
  url?: string
  reason?: string
  canTurnOn?: boolean
  turnOnHref?: string
}

/**
 * A box: a skill's real steps, or a faded one-line box for a skill not owned.
 *
 * ⚠️ THE UNBOUGHT BOX IS NOT A DARK PATTERN. Faded, no steps, no count, no
 * urgency — one line and one price, so the page does not silently pretend the
 * skill is off the menu.
 */
export interface RoadmapBox {
  title: string
  skill?: string
  state?: RoadmapState
  items: RoadmapItem[]
  doneCount: number
  total: number
  /** Set ONLY on a box the client does not own — e.g. "$75/mo". */
  price?: string
  /**
   * The committed monthly AD SPEND, ads box only (John, 2026-09-22 CC review #22):
   * "I'd want to know that, hey, I'm agreed to spend whatever, $200 a month, and I'd
   * want to see that that's here."
   *
   * ⚠️ IT LIVES ON THE BOX, NOT THE SECTION. It was first put on RoadmapSection —
   * derived, typed and unit-tested — and the roadmap page renders `view.boxes` and
   * never touches `view.sections`, so it would have been invisible. Exactly the
   * built-but-not-wired shape this review keeps turning up.
   *
   * ⚠️ `note` must ALWAYS render with `amount`. A money figure on our page with no
   * line saying where it goes reads as OUR charge.
   */
  adBudget?: { amount: string; note: string }
  /** A franchisee's scope for this skill ("Instagram only — the home office runs your Facebook"). Mirrors the backend RoadmapBox. */
  scopeNote?: string
  /**
   * 1. YOUR DETAILS — ONE row for the section: name, answered count, View / Edit
   * (a walkthrough of the section in the pop-up). No answers on the card.
   * `complete` = the card's questions row is done, so this row replaces it.
   */
  details?: {
    summary?: { title: string; answeredCount: number; complete: boolean; editHref?: string }
    roster?: { lines: string[]; editHref?: string }
  }
  /** 3. WHAT AVA MADE — this skill's samples. */
  made?: RoadmapMadeRow[]
  /** Phone only. */
  phone?: RoadmapPhoneExtras
  /** Signed off — collapse to "✓ Live" with the samples one tap away. */
  live?: boolean
  /** Nurture only: how many people we hold to follow up with, and the door to send the list. */
  followUpList?: { count: number; href?: string }
  /** Web, once the site exists (a clone's staged copy): look around it. */
  sitePreview?: { href: string; label: string }
  /**
   * The card's state in words, derived on the SERVER from its own rows
   * (agents/setupPresentation.ts, Susan's setup 2026-09-28 #11). Never computed
   * here: a browser that worked it out would be a second copy of the three gates.
   */
  status?: RoadmapBoxStatus
  /** Connections the client switched off, each with a way back (#10). Foundation box. */
  offConnections?: RoadmapOffConnection[]
  /** "How this works" — APPROVED explainers only (backend explainers/howItWorks.ts). Mirrors skillRoadmapPage.HowItWorksCard. */
  howItWorks?: RoadmapHowItWorks[]
}

/** One explainer on a setup card: the written version inline, plus its public link. */
export interface RoadmapHowItWorks {
  id: string
  title: string
  url: string
  sections: { heading: string; lines: string[] }[]
}

/** Mirrors skillRoadmapPage.BoxStatus. */
export interface RoadmapBoxStatus {
  kind: 'needs-you' | 'ready' | 'live' | 'in-progress' | 'complete'
  label: string
  needs?: string[]
  line?: string
  /** The ONE ready card the page asks for a go-ahead on right now. */
  focus?: boolean
  /** Ready, but the card named here goes first. */
  after?: string
}

/** Mirrors skillRoadmapPage.OffConnection. */
export interface RoadmapOffConnection {
  label: string
  why: 'your-choice' | 'declined'
  note: string
  turnOn: { label: string; href?: string; preferences?: Record<string, unknown> }
}

/** Mirrors skillRoadmapPage.StillToDoStep. */
export interface RoadmapStillToDoStep {
  catalogId: string
  title: string
  href?: string
}

/** "Your next steps" (John, 2026-10-08). Mirrors backend agents/setupNextSteps.ts. */
export interface RoadmapNextStepAction {
  label: string
  /** A door straight to it. */
  href?: string
  /** The skill card to open and scroll to (a go-ahead is approved on its card). */
  jumpTo?: string
}
export interface RoadmapNextStep {
  id: string
  title: string
  actions: RoadmapNextStepAction[]
  /** What becomes possible once this is done — locked, still theirs. */
  unlocks: string[]
}
export interface RoadmapNextSteps {
  steps: RoadmapNextStep[]
  /** Every client step left: the steps plus everything they unlock. */
  total: number
}

export interface RoadmapView {
  firstName: string
  greeting: string
  /**
   * Catalog provider ids this client still needs to connect — decided on the
   * SERVER, read by every surface.
   *
   * 🔴 John, 2026-09-19: "whatever shows up here in the integrations also
   * correlates and syncs with what shows up in the setup roadmap as well. They
   * should be in agreement, right?" They were not, because each surface worked
   * it out for itself. Optional only so an older cached payload still renders.
   */
  expectedIntegrationIds?: string[]
  /**
   * SKILLS fully live, and its companion `total`.
   *
   * ⚠️ LEGACY, AND DELIBERATELY NOT WHAT THE PAGE SHOWS. "0 of 5 up and running"
   * is true for every client who is not finished and is the most discouraging
   * true sentence available — a skill counts only when everything about it is
   * done, so three weeks of real work still reads as a zero. The page shows
   * STEPS. Kept only for callers that have not moved yet.
   */
  doneCount: number
  total: number
  sections: RoadmapSection[]
  /** STEPS — the honest count that can actually move. */
  stepsDone: number
  stepsTotal: number
  /** 0-100, floored, only ever 100 when every step is genuinely done. */
  percent: number
  /** The boxes, in the order they are worked through. */
  boxes: RoadmapBox[]
  /** How many steps want the client TODAY. Counted from the boxes, server-side. */
  waitingCount: number
  /** Ava's sentence over the bar, already worded. Printed verbatim. */
  sayLine: string
  /** "Your paperwork" — samples that are no skill's, plus the booking page itself. */
  paperwork?: { sections: { title: string; rows: RoadmapMadeRow[] }[]; booking?: RoadmapBookingRow }
  /** The samples' stylesheet — render samples in an iframe with exactly these rules. */
  artifactCss?: string
  /** No sample can be drawn yet (no logo). */
  previewsBlocked?: { missing: string; ask?: string }
  /** Every setup step the sequencer still owes, in order, one door each (#9). */
  stillToDo?: RoadmapStillToDoStep[]
  /** "Your next steps" — the one list every surface shows (John, 2026-10-08). */
  nextSteps?: RoadmapNextSteps
}

/**
 * How many skills are waiting on the CLIENT right now.
 *
 * ⚠️ `null` MEANS "WE COULD NOT LOAD IT", NEVER "nothing needs you". A badge
 * invented from a failed read is the same class as prose stating an unmeasured
 * status as fact — it just costs a number instead of a sentence.
 */
export function countNeedsClient(view: RoadmapView | null | undefined): number {
  if (!view) return 0
  // ⚠️ STEPS, NOT SKILLS, since 2026-09-18 — and it counts the very rows the page
  // paints amber, so the badge and the page cannot disagree. The old count was
  // skills in `now`/`signoff`, which understated the work badly: measured the day
  // it changed, Todd had NINE amber rows and the badge said 1.
  return view.waitingCount ?? 0
}
